import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { finalize, Subject, takeUntil } from 'rxjs';
import {
  LoadProcessService,
  ProcessCreateModelLike,
} from 'app/shared/services/load-process.service';
import { ToastService } from 'app/shared/services/toast.service';
import { ReadLayersService } from 'app/shared/services/read-layers.service';
import { LoadingController } from '../../../../helpers/loading.controller';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { FileUtils } from 'app/shared/utils/file.utils';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from '@services/translation.service';

interface ProcessFormLike {
  description: FormControl<string>;
  format: FormControl<number>;
  file: FormControl<string>;
}

@Component({
  selector: 'app-load-data-form',
  templateUrl: 'load-data-form.component.html',
  styleUrls: ['load-data-form.component.scss'],
})
export class LoadDataFormComponent implements OnInit, OnDestroy {
  @ViewChild('inputFileEl', { static: true })
  inputFileElRef: ElementRef<HTMLInputElement>;

  description: string;
  format: number;

  fileUrl: string;

  formGroup: FormGroup<ProcessFormLike>;

  formatList = [
    {
      text: 'ITRF93/PTRA08 - UTM zona 25N',
      value: 6,
    },
    {
      text: 'ITRF93/PTRA08 - UTM zona 26N',
      value: 7,
    },
    {
      text: 'ITRF93/PTRA08 - UTM zona 28N',
      value: 8,
    },
    {
      text: 'PT-TM06/ETRS89',
      value: 5,
    },
    {
      text: 'WGS84',
      value: 9,
    },
  ];

  get isBtnSubmitDisabled() {
    return !(this.fileUrl && this.formGroup.valid);
  }

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private loadProcessService: LoadProcessService,
    private mainMapService: MainMapService,
    private uploadService: ReadLayersService,
    private toastService: ToastService,
    private loadingControler: LoadingController,
    private screenService: ScreenService,
    private ts: TranslationService
  ) {
    this.formGroup = this.formBuilder.group<ProcessFormLike>({
      description: this.formBuilder.control<string>(null, Validators.required),
      format: this.formBuilder.control<number>(null, Validators.required),
      file: this.formBuilder.control<string>(null, Validators.required),
    });
  }

  ngOnInit(): void {
    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.formGroup.reset();
      });
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onSubmit(e) {
    e?.preventDefault();
    if (this.loadingControler.isShown() || this.isBtnSubmitDisabled) {
      return;
    }

    const file = this.inputFileElRef.nativeElement.files.item(0);

    const typeLoading = 1;

    const model: ProcessCreateModelLike = {
      description: this.formGroup.get('description').value,
      coordinateSystemId: this.formGroup.get('format').value,
      fileURL: this.fileUrl,
      fileName: file.name,
      idTipoCarregamento: typeLoading,
    };

    this.loadingControler.show();
    this.loadProcessService
      .createProcess(model)
      .pipe(
        finalize(() => {
          this.loadingControler.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: () => {
          this.toastService.success(
            this.ts.getCurrentLanguage()['process-created-successfully']
          );
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-create-process']
          );
        },
      });

    this.formGroup.reset();
  }

  onFileChange(e) {
    this.fileUrl = null;
    const file = this.inputFileElRef.nativeElement.files?.item(0);
    if (file) {
      if (!FileUtils.isFileExt(file, 'zip')) {
        this.toastService.error(
          this.ts.getCurrentLanguage()['only-zip-allowed']
        );
        return;
      }

      this.loadingControler.show();
      this.uploadService
        .generateLayerUrlFromFile(file)
        .pipe(
          finalize(() => {
            this.loadingControler.hide();
          }),
          takeUntil(this.lifecycle)
        )
        .subscribe({
          next: (url) => {
            this.fileUrl = url;

            this.toastService.success(
              `${this.ts.getCurrentLanguage().file} "${file.name}" ${
                this.ts.getCurrentLanguage()['uploaded-successfully']
              }`
            );
          },
          error: (response) => {
            if (!response) {
              return;
            }

            this.toastService.error(
              this.ts.getCurrentLanguage()['failed-read-file']
            );
          },
        });
    }
  }

  onBtnSearchFileClick() {
    this.inputFileElRef?.nativeElement?.click();
  }
}
